import { uniqWith } from 'es-toolkit';
import React from 'react';
import { Line } from '@react-three/drei';

import { LineSegment3D } from '@paragon/geometry-library';
import { pairwise, sortBy } from '@paragon/utilities';

import { useViewCenter } from '../../../viewCenter';
import { Segment, Segments } from '../../Segments';
import { getPolygonFaceRenderOrder } from '../common/renderOrder';
import { LayoutPolygonsEdgesCollection } from './LayoutPolygonsEdgesCollection';

const NEVER_RAYCAST = () => {};

/**
 * Renders all the edges of a set of layout polygons, consolidating overlapping lines, respecting render order, and
 *   ensuring that the correct style is applied to each edge.
 */
export function LayoutPolygonsEdgesRenderer(props: { segments: LayoutPolygonsEdgesCollection }) {
  // Naively rendering the edges of roof faces as-is leads to many overlapping lines. A lot of processing must be done
  //   to consolidate overlapping lines into single or multiple segments with the correct styles based on the desired
  //   precedence of roof plane styles.
  //
  // All of that work happens here. If you have any ideas on how to clean this up, please do so.

  const viewCenter = useViewCenter(props.segments.hasLines());

  const segments = sortBy(props.segments.getSegments(viewCenter), (line) => -getPolygonFaceRenderOrder(line.color));

  for (let i = 0; i < segments.length; i++) {
    const workingSegment = segments[i].segment;

    for (let j = i + 1; j < segments.length; j++) {
      const otherSegment = segments[j].segment;

      if (workingSegment.overlaps(otherSegment)) {
        const uniquePoints = uniqWith(
          [workingSegment.basePoint, workingSegment.endPoint, otherSegment.basePoint, otherSegment.endPoint],
          (a, b) => a.equals(b),
        );

        const sortedPoints = sortBy(uniquePoints, (point) =>
          point.projectedSignedDistanceAlong(workingSegment.toLine()),
        );

        const squashedSegments = pairwise(sortedPoints)
          .map(([basePoint, endPoint]) => new LineSegment3D(basePoint, endPoint))
          .map((segment) => {
            const color = workingSegment.contains(segment.midpoint) ? segments[i].color : segments[j].color;
            const dashed = workingSegment.contains(segment.midpoint) ? segments[i].dashed : segments[j].dashed;

            return {
              segment,
              color,
              dashed,
            };
          });

        segments.splice(j, 1);
        segments.splice(i, 1, ...squashedSegments);

        i -= 1;

        break;
      }
    }
  }

  return (
    <Segments initialSize={100} sizeIncrement={100} lineWidth={2}>
      {segments.map(({ segment, color, dashed }) => {
        const key = `${segment.basePoint.x},${segment.basePoint.y},${segment.basePoint.z}-${segment.endPoint.x},${segment.endPoint.y},${segment.endPoint.z}`;

        const basePoint = segment.basePoint.toArray();
        const endPoint = segment.endPoint.toArray();

        return dashed ? (
          <Line dashed key={key} points={[basePoint, endPoint]} color={color} lineWidth={2} raycast={NEVER_RAYCAST} />
        ) : (
          <Segment key={key} start={basePoint} end={endPoint} color={color} />
        );
      })}
    </Segments>
  );
}
